import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import {
  getOrCreateDeviceUuid,
  getPlaidSnapshots as getLocalSnapshots,
  PlaidSnapshot as LocalPlaidSnapshot,
} from '../storage';
import { getPlaidSnapshots as getBackendSnapshots, PlaidSnapshot as BackendPlaidSnapshot } from '../apis/plaid';

interface ChartElement {
  price: number;
  date: Date;
  minute: string;
  changeRatio?: number;
}

// Unified snapshot interface for internal use
interface Snapshot {
  date: string;
  totalValue: number;
}

/**
 * Hook to manage Plaid portfolio snapshots for chart display.
 * Fetches snapshots from backend API (with local storage fallback) and transforms
 * them into chart-compatible format.
 */
export const usePlaidSnapshots = () => {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Shared function to fetch snapshots from backend with local storage fallback
  const fetchSnapshots = async (): Promise<Snapshot[]> => {
    const deviceUuid = await getOrCreateDeviceUuid();

    // Try backend first - if it succeeds (even with empty array), use it
    try {
      const response = await getBackendSnapshots(deviceUuid);
      return (response.snapshots || []).map((s: BackendPlaidSnapshot) => ({
        date: s.snapshotDate,
        totalValue: s.totalValue,
      }));
    } catch (error) {
      // Fallback to local storage if backend fails
      console.warn('Backend snapshot fetch failed, falling back to local storage:', error);
    }

    // Fallback to local storage only if backend failed
    const localData = await getLocalSnapshots();
    return localData.map((s: LocalPlaidSnapshot) => ({
      date: s.date,
      totalValue: s.totalValue,
    }));
  };

  // Load snapshots on mount
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchSnapshots();
        setSnapshots(data);
      } catch (error) {
        console.warn('Error loading Plaid snapshots:', error);
      }
      setIsLoading(false);
    };
    load();
  }, []);

  // Reload snapshots from backend and local storage
  const reload = async () => {
    try {
      const data = await fetchSnapshots();
      setSnapshots(data);
    } catch (error) {
      console.warn('Error reloading Plaid snapshots:', error);
    }
  };

  // Sort snapshots by date ascending (memoized for reuse)
  // Returns all snapshots sorted, including single-snapshot case for latestSnapshot
  const sortedSnapshots = useMemo(() => {
    if (snapshots.length === 0) return [];
    return [...snapshots].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [snapshots]);

  // Transform snapshots to ChartElement[]
  const toChartElements = (items: Snapshot[], baseValue: number): ChartElement[] =>
    items.map((s) => ({
      price: s.totalValue,
      date: new Date(s.date),
      minute: format(new Date(s.date), 'HH:mm'),
      changeRatio: baseValue > 0 ? (s.totalValue - baseValue) / baseValue : 0,
    }));

  // Adaptive chart with ALL available data (for Plaid portfolios)
  const allDataChart: ChartElement[] = useMemo(() => {
    if (sortedSnapshots.length < 2) return [];
    const baseValue = sortedSnapshots[0].totalValue;
    return toChartElements(sortedSnapshots, baseValue);
  }, [sortedSnapshots]);

  // Start date of the data (first snapshot)
  const startDate: Date | null = useMemo(() => {
    if (sortedSnapshots.length === 0) return null;
    return new Date(sortedSnapshots[0].date);
  }, [sortedSnapshots]);

  // Number of days between first and last snapshot
  const dataSpanDays: number = useMemo(() => {
    if (sortedSnapshots.length < 2) return 0;
    const first = new Date(sortedSnapshots[0].date);
    const last = new Date(sortedSnapshots[sortedSnapshots.length - 1].date);
    return Math.round((last.getTime() - first.getTime()) / (1000 * 60 * 60 * 24));
  }, [sortedSnapshots]);

  // Calculate total return from first to last snapshot
  const totalReturn = useMemo(() => {
    if (sortedSnapshots.length < 2) return 0;
    const first = sortedSnapshots[0].totalValue;
    const last = sortedSnapshots[sortedSnapshots.length - 1].totalValue;
    return first > 0 ? (last - first) / first : 0;
  }, [sortedSnapshots]);

  // Need at least 5 snapshots to show a meaningful trend line
  const MIN_SNAPSHOTS_FOR_CHART = 5;

  return {
    allDataChart, // Single chart with all available data (for adaptive display)
    startDate, // Date of first snapshot (for "Since [date]" label)
    dataSpanDays, // Days between first and last snapshot
    isLoading,
    hasEnoughData: snapshots.length >= MIN_SNAPSHOTS_FOR_CHART,
    latestSnapshot: sortedSnapshots.length > 0 ? sortedSnapshots[sortedSnapshots.length - 1] : null, // Latest snapshot (after sorting ascending, last is newest)
    totalReturn,
    reload,
  };
};
